import type { ManagerOptions, Socket, SocketOptions } from 'socket.io-client';
import { io } from 'socket.io-client';
import SignatureSocketClient from './SignatureSocketClient';
import type { BlockEvents, DocumentSettingsEvents, SectionEvents } from './SocketEvents';
import { SignatureEvents, SocketClientDefaultEvents, SocketConnectionEvents } from './SocketEvents';
import {
  type SanitizedAssignedSignee,
  type SignatureBox,
  type SignatureBoxApiResponse,
  type SectionsSignaturesType,
} from 'services/entities/SignatureTypes';
import { transformSignatureBoxApiResponse } from 'services/repositories/implementations/ApiSignatureDocumentServiceRepository';

import { v4 as uuidv4 } from 'uuid';

type AuthObject = {
  contentId: string | undefined;
};

export type GetAllSignaturesOnMountCallback = (data: SectionsSignaturesType) => void;
type SignatureInsertionType = SignatureBox & { sectionId: string };
export type SignaturePropertyUpdate = Pick<SignatureBox, 'signatureBoxId' | 'properties'>;
export type SignatureReassignPayload = {
  signatureBoxId: string;
  assignedSignee: SanitizedAssignedSignee | null;
};

export type TSocketCallback = (data: any) => void;

export type Events =
  | DocumentSettingsEvents
  | SignatureEvents
  | BlockEvents
  | SectionEvents
  | SocketConnectionEvents
  | SocketClientDefaultEvents;

const SubscribeEventsToAlwaysRunCallbacks: Events[] = [SocketConnectionEvents.CONNECTED, SocketConnectionEvents.TOKEN_EXPIRED];

class SocketClient {
  // SP-2151 - Remove this attribute; during migration, we will want callbacks related to reading to be executed only from the document service
  private readonly disableCallbacks: boolean;

  private signatureSocketClient: SignatureSocketClient;
  private isConnected = false;
  private socketClient: Socket;
  private pendingRequests: Record<
    string,
    { event: Events; data: any; callback: TSocketCallback; options?: { shouldDisconnectAfterReplay: boolean } }
  > = {};

  private connectionParams: Partial<ManagerOptions & SocketOptions> & { auth: AuthObject } = {
    autoConnect: false,
    reconnection: true,
    reconnectionDelay: 400,
    reconnectionAttempts: 5,
    auth: {
      contentId: undefined,
    },
    transports: ['websocket'],
    withCredentials: true,
  };
  serverUrl: string;

  constructor(serverURL: string, authParams: AuthObject, disableCallbacks = false) {
    this.disableCallbacks = disableCallbacks;
    this.serverUrl = serverURL;
    this.socketClient = this.getNewSocketClient(serverURL, authParams);
    this.signatureSocketClient = new SignatureSocketClient(this);

    this.socketClient.on(SocketClientDefaultEvents.DISCONNECTED, () => {
      this.isConnected = false;
    });

    this.socketClient.on(SocketClientDefaultEvents.CONNECTION_ERROR, () => {
      this.isConnected = false;
    });
  }

  private getNewSocketClient(serverURL: string, authParams: AuthObject): Socket {
    this.connectionParams.auth = authParams;
    return io(serverURL, this.connectionParams);
  }

  public getIsConnected(): boolean {
    return this.isConnected;
  }

  public getIsCallbacksDisabled(): boolean {
    return this.disableCallbacks;
  }

  public hasPendingRequests(): boolean {
    return Object.keys(this.pendingRequests).length > 0;
  }

  public reconnect(): void {
    this.socketClient.disconnect();
    this.socketClient.auth = this.connectionParams.auth;
    this.connect();
  }

  public connect(callback?: () => void): void {
    this.subscribeOnce(SocketConnectionEvents.CONNECTED, () => {
      this.isConnected = true;
      this.replayPendingRequests();
      callback?.();
    });

    this.socketClient.connect();
  }

  public disconnect() {
    this.socketClient.disconnect();
    this.isConnected = false;
  }

  public getAllSignaturesOnMount(callback: GetAllSignaturesOnMountCallback) {
    this.subscribeOnce(SignatureEvents.SIGNATURE_GET_ALL, (response: { [sectionId: string]: SignatureBoxApiResponse[] } = {}) => {
      const data: SectionsSignaturesType = {};

      for (const [sectionId, signatures] of Object.entries(response)) {
        data[sectionId] = signatures.map((signature) => {
          return transformSignatureBoxApiResponse(signature);
        });
      }

      callback(data);
    });
  }

  private shouldIgnoreSubscribeEventCallback(event: Events): boolean {
    return this.disableCallbacks && !SubscribeEventsToAlwaysRunCallbacks.includes(event);
  }

  public subscribe(event: Events, callback: any): SocketClient {
    if (this.shouldIgnoreSubscribeEventCallback(event)) {
      callback = () => {
        // DO NOTHING
      };
    }

    this.socketClient.on(event, callback);
    return this;
  }

  public unSubscribe(event: Events, callback: any): SocketClient {
    if (this.shouldIgnoreSubscribeEventCallback(event)) {
      callback = () => {
        // DO NOTHING
      };
    }

    this.socketClient.off(event, callback);
    return this;
  }

  public subscribeOnce(event: Events, callback: any): SocketClient {
    if (this.shouldIgnoreSubscribeEventCallback(event)) {
      callback = () => {
        // DO NOTHING
      };
    }

    this.socketClient.once(event, callback);
    return this;
  }

  public publish(event: Events, data: object, callback: (resp: any) => any, options?: { force: boolean }): SocketClient {
    if (this.disableCallbacks) {
      callback = () => {
        // DO NOTHING
      };
    }

    const requestId = uuidv4();
    let shouldDisconnectAfterReplay = false;
    if (options?.force && !this.getIsConnected()) {
      shouldDisconnectAfterReplay = true;
      this.connect();
    }

    const dataToSend = { ...data, traceId: requestId };
    this.pendingRequests[requestId] = { event, data: dataToSend, callback, options: { shouldDisconnectAfterReplay } };

    if (this.getIsConnected()) {
      this.socketClient.emit(event, dataToSend, (response: any) => {
        delete this.pendingRequests[requestId];
        callback(response);
      });
    }

    return this;
  }

  public addSignatureContent(data: SignatureInsertionType, callback: TSocketCallback) {
    this.signatureSocketClient.publishSignatureContent(data, SignatureEvents.SIGNATURE_ADD, callback);
  }

  public updateSignatureContent(data: SignaturePropertyUpdate, callback: TSocketCallback) {
    this.signatureSocketClient.publishSignatureContent(data, SignatureEvents.SIGNATURE_UPDATE, callback);
  }

  public deleteSignatureContent(data: SignatureBox, callback: TSocketCallback) {
    this.signatureSocketClient.publishSignatureContent(data, SignatureEvents.SIGNATURE_DELETE, callback);
  }

  private replayPendingRequests(): void {
    let shouldDisconnectAfterReplay = false;

    for (const requestId of Object.keys(this.pendingRequests)) {
      const request = this.pendingRequests[requestId];
      delete this.pendingRequests[requestId];

      const numberOfPendingRequests = Object.keys(this.pendingRequests).length;
      const { event, data, callback: requestCallback, options } = request;

      const callback: TSocketCallback = (response) => {
        if (options?.shouldDisconnectAfterReplay) {
          shouldDisconnectAfterReplay = true;
        }
        requestCallback(response);

        if (shouldDisconnectAfterReplay && numberOfPendingRequests === 0) {
          this.disconnect();
        }
      };

      this.publish(event, data, callback);
    }
  }
}

export default SocketClient;
